import type { AllGradeSubject, Revision, SchoolType, Semester } from '~/@entities/SchoolSystem'

import type { ScorableProblem, ScoringItem } from './schemas'

// 다중 셀 답은 콤마로 잇는다. 미입력 칸은 \square, \sum·\prod 는 인라인 KaTeX 가 첨자를 옆에 붙여 \limits 로
// 보정하고, \allowbreak 로 수식 중간이 아닌 답 사이에서만 줄바꿈을 허용한다.
// 키패드가 남긴 \placeholder 는 KaTeX 명령이 아니라 파싱 에러가 나므로 \square 로 바꾼다. ☐(U+2610) 는
// KaTeX 폰트 메트릭이 없어 폭 0 으로 그려지므로 쓰지 않는다.
export const formatSubmittedAnswer = ({
  userAnswer,
  userAnswers,
}: Pick<ScoringItem<unknown, ScorableProblem>, 'userAnswer' | 'userAnswers'>): string | null => {
  if (userAnswers?.length) {
    return userAnswers
      .map((cell) =>
        withoutPlaceholder(cell ?? '\\square').replace(/\\(sum|prod)(?=[_^])/g, '\\$1\\limits'),
      )
      .join(',\\; \\allowbreak ')
  }
  return userAnswer && withoutPlaceholder(userAnswer)
}

const withoutPlaceholder = (latex: string) => latex.replace(/\\placeholder(\{\})?/g, '\\square ')

// 고등은 과목명을 그대로 쓰고, 그 밖은 "중1"·"초3" 처럼 붙인다. 학교급을 모르면 초등으로 읽는다.
export const formatWorksheetScoringGrade = (
  schoolType: SchoolType | undefined,
  grade: AllGradeSubject | undefined,
) => {
  if (schoolType === 'HIGH') return `${grade}`
  return (schoolType === 'MIDDLE' ? '중' : '초') + grade
}

// 교재는 개정과 학기까지 붙인다. 15개정만 개정 표기를 생략한다. 예: "중 1(22)개정-1"
export const formatWorkbookScoringGrade = ({
  schoolType,
  grade,
  semester,
  revision,
}: {
  schoolType?: SchoolType
  grade?: AllGradeSubject
  semester?: Semester
  revision?: Revision
}) => {
  if (schoolType === 'HIGH') return grade as string
  const schoolTypeKo = schoolType === 'MIDDLE' ? '중' : '초'
  const revisionKo = revision === 'CURRICULUM_15' ? '' : '(22)개정'
  return `${schoolTypeKo} ${grade}${revisionKo}-${semester}`
}
